import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  Autocomplete,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { AddCircle, Delete, Inventory2Outlined, InfoOutlined, ListAlt } from '@mui/icons-material';
import { api } from '../../services/api';

export interface AvailableItem {
  id: number;
  name: string;
  units_per_carton?: number;
}

export interface OutboundOrderItem {
  item_id: number;
  item_name: string;
  currentStock: number | null;
  orderQuantity: number;
  pendingQuantity: number;
  prevPendingQuantity: number | null;
  note: string;
  loadingStock: boolean;
}

interface PendingItem {
  id: number;
  partner_id: number;
  item_id: number;
  order_id: number;
  pending_qty: number;
  previous_pending_qty: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface OutboundItemsTableProps {
  items: OutboundOrderItem[];
  availableItems: AvailableItem[];
  onItemsChange: (items: OutboundOrderItem[]) => void;
  partnerId?: number;
}

const OutboundItemsTable: React.FC<OutboundItemsTableProps> = ({
  items,
  availableItems,
  onItemsChange,
  partnerId,
}) => {
  const [searchLoading, setSearchLoading] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerItems, setLedgerItems] = useState<PendingItem[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerItemName, setLedgerItemName] = useState('');

  const fetchPendingLedger = async (itemId: number, itemName: string) => {
    if (!partnerId) return;
    setLedgerItemName(itemName);
    setLedgerOpen(true);
    setLedgerLoading(true);
    try {
      const response = await api.get(`/api/pending-items/ledger/${partnerId}/items/${itemId}`);
      setLedgerItems(response.data.pending_items ?? []);
    } catch (err) {
      console.error('Error fetching pending ledger:', err);
      setLedgerItems([]);
    } finally {
      setLedgerLoading(false);
    }
  };
  
  // Create refs for input fields
  const inputRefs = useRef<{
    [key: string]: HTMLInputElement | null;
  }>({});

  const fetchItemStock = async (itemId: number): Promise<number> => {
    try {
      const response = await api.get(`/api/items/${itemId}/stock`);
      return response.data.available_quantity ?? response.data.quantity ?? 0;
    } catch (err) {
      console.error('Error fetching stock:', err);
      return 0;
    }
  };

  const fetchItemPendingQuantiy = async (partnerId: number, itemId: number): Promise<number> => {
    try {
      const response = await api.get(`/api/pending-items/${partnerId}/items/${itemId}`);
      return response.data.pending_item.pending_qty ?? 0;
    } catch (err) {
      console.error('Error fetching stock:', err);
      return 0;
    }
  };

  const handleItemSelect = async (index: number, selectedItem: AvailableItem | null) => {
    const newItems = [...items];
    
    if (selectedItem) {
      // Set loading state
      newItems[index] = {
        ...newItems[index],
        item_id: selectedItem.id,
        item_name: selectedItem.name,
        loadingStock: true,
        currentStock: null,
        orderQuantity: 0,
        pendingQuantity: 0,
      };
      onItemsChange(newItems);

      // Fetch stock for the selected item
      const stock = await fetchItemStock(selectedItem.id);
      const prevPendingQuantity = partnerId ? await fetchItemPendingQuantiy(partnerId, selectedItem.id) : 0;
      
      // Update with stock data
      const updatedItems = [...newItems];
      updatedItems[index] = {
        ...updatedItems[index],
        currentStock: stock,
        loadingStock: false,
        prevPendingQuantity: prevPendingQuantity
      };
      onItemsChange(updatedItems);
      
      // Focus on Order Qty field after item selection
      setTimeout(() => {
        const orderQtyRef = inputRefs.current[`${index}-orderQty`];
        if (orderQtyRef) orderQtyRef.focus();
      }, 50);
    } else {
      // Clear the item
      newItems[index] = {
        item_id: 0,
        item_name: '',
        currentStock: null,
        orderQuantity: 0,
        pendingQuantity: 0,
        prevPendingQuantity: null,
        note: '',
        loadingStock: false,
      };
      onItemsChange(newItems);
    }
  };

  const handleQuantityChange = (
    index: number,
    field: 'orderQuantity' | 'pendingQuantity',
    value: string
  ) => {
    const newItems = [...items];
    const item = newItems[index];
    
    // Allow only digits
    const onlyDigits = value.replace(/[^\d]/g, '');
    const numValue = onlyDigits === '' ? 0 : parseInt(onlyDigits, 10);
    
    if (isNaN(numValue) || numValue < 0) return;

    if (field === 'orderQuantity') {
      // Cap order quantity to current stock
      const maxStock = item.currentStock ?? 0;
      const validValue = Math.min(numValue, maxStock);
      newItems[index] = { ...item, orderQuantity: validValue };
    } else {
      // Pending quantity - no max limit
      newItems[index] = { ...item, pendingQuantity: numValue };
    }
    
    onItemsChange(newItems);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentIndex: number,
    currentField: 'orderQuantity' | 'pendingQuantity'
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      
      // Move to next field
      if (currentField === 'orderQuantity') {
        // Focus pending quantity in the same row
        const nextRef = inputRefs.current[`${currentIndex}-pendingQty`];
        if (nextRef) nextRef.focus();
      } else if (currentField === 'pendingQuantity') {
        const nextIndex = currentIndex + 1;
        const nextRowHasItem = items[nextIndex]?.item_id > 0;
        const nextRef = nextRowHasItem
          ? inputRefs.current[`${nextIndex}-orderQty`]
          : inputRefs.current[`${nextIndex}-itemSearch`];
        if (nextRef) {
          nextRef.focus();
        } else {
          addItemRow();
          setTimeout(() => {
            const newRef = inputRefs.current[`${nextIndex}-itemSearch`];
            if (newRef) newRef.focus();
          }, 50);
        }
      }
    }
  };

  const addItemRow = () => {
    onItemsChange([
      ...items,
      {
        item_id: 0,
        item_name: '',
        currentStock: null,
        orderQuantity: 0,
        pendingQuantity: 0,
        prevPendingQuantity: null,
        note: '',
        loadingStock: false,
      },
    ]);
  };

  const removeItemRow = (index: number) => {
    onItemsChange(items.filter((_, i) => i !== index));
  };

  const getSelectedItem = (item: OutboundOrderItem): AvailableItem | null => {
    if (item.item_id === 0) return null;
    return availableItems.find((i) => i.id === item.item_id) || null;
  };

  // Get available items for a row, excluding items already selected in other rows
  const getAvailableItemsForRow = (currentIndex: number): AvailableItem[] => {
    const selectedItemIds = items
      .filter((_, i) => i !== currentIndex)
      .map((item) => item.item_id)
      .filter((id) => id > 0);
    
    return availableItems.filter((item) => !selectedItemIds.includes(item.id));
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Order Items
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5, width: '100%' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold', width: '34%' }}>Item</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="center">Item Info</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="center">Stock</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '17%' }} align="center">Order Qty</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '15%' }} align="center">Pending Qty</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '14%' }} align="center">Note</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '8%' }} align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index} hover>
                {/* Item Search */}
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={getAvailableItemsForRow(index)}
                    getOptionLabel={(option) => option.name}
                    value={getSelectedItem(item)}
                    onChange={(_, newValue) => handleItemSelect(index, newValue)}
                    filterOptions={(options, { inputValue }) => {
                      const filterValue = inputValue.toLowerCase();
                      return options.filter((option) =>
                        option.name.toLowerCase().includes(filterValue)
                      );
                    }}
                    ListboxProps={{
                      style: { maxHeight: 200 }, // ~5 items visible
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search item..."
                        size="small"
                        inputRef={(ref) => {
                          if (ref) inputRefs.current[`${index}-itemSearch`] = ref;
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Typography variant="body2">{option.name}</Typography>
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                </TableCell>

                {/* Units per Carton */}
                <TableCell align="center">
                  {item.item_id > 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="caption" display="block">
                              Units/CTN: {getSelectedItem(item)?.units_per_carton ?? '-'}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Prev Pending Qty: {item.prevPendingQuantity ?? '-'}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <InfoOutlined sx={{ fontSize: 16, color: 'action.active', cursor: 'pointer' }} />
                      </Tooltip>
                      <Tooltip title="View pending ledger" arrow>
                        <ListAlt
                          sx={{ fontSize: 16, color: 'primary.main', cursor: 'pointer' }}
                          onClick={() => fetchPendingLedger(item.item_id, item.item_name)}
                        />
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.disabled">-</Typography>
                  )}
                </TableCell>

                {/* Current Stock */}
                <TableCell align="center">
                  {item.item_id > 0 ? (
                    item.loadingStock ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Chip
                        icon={<Inventory2Outlined fontSize="small" />}
                        label={item.currentStock ?? 0}
                        size="small"
                        color={item.currentStock === 0 ? 'error' : 'success'}
                        variant="outlined"
                      />
                    )
                  ) : (
                    <Typography variant="body2" color="text.disabled">-</Typography>
                  )}
                </TableCell>

                {/* Order Quantity */}
                <TableCell align="center">
                  <TextField
                    size="small"
                    type="text"
                    inputMode="numeric"
                    value={item.orderQuantity || ''}
                    onChange={(e) =>
                      handleQuantityChange(index, 'orderQuantity', e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                      handleKeyDown(e, index, 'orderQuantity')
                    }
                    inputRef={(ref) => {
                      if (ref) inputRefs.current[`${index}-orderQty`] = ref;
                    }}
                    disabled={item.item_id === 0 || item.loadingStock}
                    inputProps={{ min: 0, style: { textAlign: 'center', width: 80 } }}
                    placeholder="0"
                  />
                </TableCell>

                {/* Pending Quantity */}
                <TableCell align="center">
                  <TextField
                    size="small"
                    type="text"
                    inputMode="numeric"
                    value={item.pendingQuantity || ''}
                    onChange={(e) =>
                      handleQuantityChange(index, 'pendingQuantity', e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                      handleKeyDown(e, index, 'pendingQuantity')
                    }
                    inputRef={(ref) => {
                      if (ref) inputRefs.current[`${index}-pendingQty`] = ref;
                    }}
                    disabled={item.item_id === 0 || item.loadingStock}
                    inputProps={{ min: 0, style: { textAlign: 'center', width: 80 } }}
                    placeholder="0"
                  />
                </TableCell>

                {/* Note */}
                <TableCell align="center">
                  <TextField
                    size="small"
                    value={item.note || ''}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[index] = { ...newItems[index], note: e.target.value };
                      onItemsChange(newItems);
                    }}
                    disabled={item.item_id === 0}
                    placeholder="Note"
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                </TableCell>

                {/* Delete Action */}
                <TableCell align="center">
                  {items.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeItemRow(index)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button startIcon={<AddCircle />} onClick={addItemRow} size="small">
          Add Item
        </Button>
      </Box>

      <Dialog open={ledgerOpen} onClose={() => setLedgerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Pending Ledger — {ledgerItemName}</DialogTitle>
        <DialogContent>
          {ledgerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : ledgerItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>No pending records found.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell align="right">Updated Pending Qty</TableCell>
                  <TableCell align="right">Previous Pending Qty</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledgerItems.map((pi) => (
                  <TableRow key={pi.id}>
                    <TableCell>{pi.order_id}</TableCell>
                    <TableCell align="right">{pi.pending_qty}</TableCell>
                    <TableCell align="right">{pi.previous_pending_qty}</TableCell>
                    <TableCell>{new Date(pi.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OutboundItemsTable;
